<template>
  <div class="trend-card trend-card--recentloans">
    <div class="trend-card__header trend-card__header--tight">
      <div class="trend-card__title">
        <i class="fa-duotone fa-clock-rotate-left"></i>
        Recently Opened Loans
      </div>
      <span class="trend-card__meta">{{ recentLoans.length }}</span>
    </div>

    <div class="trend-card__body trend-card__body--recentloans">
      <div class="trend-loading notes-empty" v-if="recentLoansLoading">
        Loading...
      </div>

      <div class="trend-empty notes-empty" v-else-if="!recentLoans.length">
        No loans opened yet
      </div>

      <div v-else class="recent-loans">
        <div
          v-for="l in recentLoans"
          :key="l.loanId"
          class="recent-loan"
          @click="$emit('openLoan', l.loanId, null)"
          :title="`Open Loan ${l.loanNumber || ''}`"
        >
          <div class="recent-loan__top">
            <div class="recent-loan__titleline">
              <div class="recent-loan__num">
                <i class="fa-duotone fa-file-invoice"></i>
                {{ l.loanNumber || "Loan" }}
              </div>

              <div class="recent-loan__borrower-inline">
                {{ l.borrowerName || "—" }}
              </div>
      <div class="recent-loan__borrower-inline">
                  <i class="fa-duotone fa-location-dot"></i>
              {{ l.cityState || "—" }}
              </div>
          
            </div>

            <div class="recent-loan__time">
              {{ timeAgo(l.openedAt) }}
            </div>

          </div>

          <div class="recent-loan__meta">
            <span class="meta-chip">
              <i class="fa-duotone fa-sack-dollar"></i>
              {{ formatMoney(l.loanAmount) }}
            </span>
           
<span class="meta-chip meta-chip--type">
  <i class="fa-duotone fa-file-signature"></i>
  {{ l.f1172 }}
</span>

<span class="meta-chip meta-chip--purpose">
  <i class="fa-duotone fa-bullseye-arrow"></i>
  {{ l.f19 }}
</span>


          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from "@/api/api.service.js"
import SignalRService from "@/api/signalr.service"

export default {
  name: "InitialUwRecentLoansCard",
  props: {
    azureId: { type: String, required: true },
    authToken: { type: String, required: true }
  },
  data() {
    return {
      recentLoans: [],
      recentLoansLoading: false,
      _recentLoanHandlerBound: false,
      _refreshLoanDebounce: null
    }
  },
  methods: {
    async fetchRecentLoans() {
      if (!this.azureId || !this.authToken) return
      if (this.recentLoansLoading) return

      this.recentLoansLoading = true
      try {
        const resp = await ApiService.GetRecentLoans(this.azureId, this.authToken, 10)
        const data = resp?.data?.data || resp?.data || []

        if (!Array.isArray(data)) {
          this.recentLoans = []
          return
        }

        const mapped = data
          .map(x => this.normalizeLoan(x))
          .filter(Boolean)

        this.recentLoans = this.sortLoans(mapped).slice(0, 10)
      } catch {
        this.recentLoans = []
      } finally {
        this.recentLoansLoading = false
      }
    },

    normalizeLoan(x) {
      if (!x) return null
      const loanId = x.loanId || x.LoanId
      if (!loanId) return null

      return {
        loanId,
        loanNumber: x.loanNumber || x.LoanNumber || "",
        borrowerName: x.borrowerName || x.BorrowerName || "",
        loanAmount: x.loanAmount ?? x.LoanAmount ?? null,
        cityState: x.cityState || x.CityState || "",
        openedAt: x.openedAt || x.OpenedAt || null,
        f1172:x.f1172,
        f19:x.f19
      }
    },

    sortLoans(list) {
      return (Array.isArray(list) ? list : []).slice().sort((a, b) => {
        const aa = a?.openedAt ? new Date(a.openedAt).getTime() : 0
        const bb = b?.openedAt ? new Date(b.openedAt).getTime() : 0
        return bb - aa
      })
    },

    upsertIntoList(loan) {
      if (!loan?.loanId) return

      const idx = this.recentLoans.findIndex(r => r.loanId === loan.loanId)

      if (idx >= 0) {
        const updated = { ...this.recentLoans[idx], ...loan }
        this.recentLoans.splice(idx, 1)
        this.recentLoans.unshift(updated)
      } else {
        this.recentLoans.unshift(loan)
      }

      this.recentLoans = this.sortLoans(this.recentLoans).slice(0, 10)
    },

    async refreshLoanById(loanId) {
      if (!loanId || !this.azureId || !this.authToken) return

      clearTimeout(this._refreshLoanDebounce)
      this._refreshLoanDebounce = setTimeout(async () => {
        try {
          const resp = await ApiService.GetRecentLoans(this.azureId, this.authToken, 25)
          const data = resp?.data?.data || resp?.data || []
          if (!Array.isArray(data)) return

          const found = data.find(x => (x.loanId || x.LoanId) === loanId)
          const normalized = this.normalizeLoan(found)
          if (normalized) this.upsertIntoList(normalized)
        } catch {}
      }, 150)
    },

    async startSignalR() {
      if (!this.azureId) return

      await SignalRService.startConnection(this.azureId)
      await SignalRService.joinRecentLoansGroup(this.azureId)

      if (!this._recentLoanHandlerBound) {
        SignalRService.onRecentLoanUpdated(message => {
          const dto = message?.data || message?.Data || message
          const loan = this.normalizeLoan(dto)
          if (!loan?.loanId) return

          this.upsertIntoList(loan)

          const isPartial =
            !loan.loanNumber &&
            !loan.borrowerName &&
            !loan.cityState &&
            !loan.openedAt

          if (isPartial) {
            this.refreshLoanById(loan.loanId)
          }
        })

        this._recentLoanHandlerBound = true
      }
    },

    async stopSignalR() {
      if (!this.azureId) return
      if (!SignalRService?.isConnectionActive?.()) return
      await SignalRService.leaveRecentLoansGroup(this.azureId)
    },

    timeAgo(value) {
      const d = new Date(value)
      if (!d || isNaN(d.getTime())) return ""
      const diff = Date.now() - d.getTime()
      const mins = Math.floor(diff / 60000)
      if (mins < 1) return "just now"
      if (mins < 60) return `${mins}m ago`
      const hrs = Math.floor(mins / 60)
      if (hrs < 24) return `${hrs}h ago`
      const days = Math.floor(hrs / 24)
      return `${days}d ago`
    },

    formatMoney(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return "—"
      return `$${Math.round(n).toLocaleString()}`
    }
  },

  async mounted() {
    await this.fetchRecentLoans()
    await this.startSignalR()
  },

  async beforeUnmount() {
    await this.stopSignalR()
    clearTimeout(this._refreshLoanDebounce)
  },

  watch: {
    async azureId(nv, ov) {
      if (!nv || nv === ov) return
      if (ov) {
        await SignalRService.leaveRecentLoansGroup(ov)
      }
      await this.fetchRecentLoans()
      await this.startSignalR()
    },
    async authToken() {
      await this.fetchRecentLoans()
    }
  }
}
</script>

<style scoped>
.trend-card {
  background: var(--background);
  border: 1px solid var(--border-ltr);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.trend-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border-ltr);
  background: rgba(5, 29, 58, 0.02);
}

.trend-card__header--tight {
  padding: 9px 10px;
}

.trend-card__title {
  font-size: 13px;
  font-weight: 950;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--text-primary-on-background);
}

.trend-card__meta {
  font-size: 12px;
  font-weight: 800;
  color: var(--text-secondary-on-background);
}

.trend-card--recentloans .trend-card__title i.fa-duotone {
  --fa-primary-color: rgba(10, 170, 120, 0.95);
  --fa-secondary-color: rgba(10, 170, 120, 0.26);
}

.trend-card__body--recentloans {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 10px 10px 12px;
}

.trend-loading,
.trend-empty {
  height: 210px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  color: var(--text-secondary-on-background);
  gap: 10px;
}

.recent-loans {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.recent-loan {
  border: 1px solid var(--border-ltr);
  border-radius:  12px;
  padding: 8px 8px;
  background: rgba(5, 29, 58, 0.02);
  cursor: pointer;
  transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease;
}

.recent-loan:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.06);
  border-color: rgba(40, 110, 255, 0.22);
}

.recent-loan__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.recent-loan__titleline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.recent-loan__num {
  font-weight: 950;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.recent-loan__borrower-inline {
  font-weight: 900;
  font-size: 12px;
  color: var(--text-primary-on-background);
  opacity: 0.9;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.recent-loan__time {
  font-weight: 850;
  font-size: 11px;
  opacity: 0.75;
  white-space: nowrap;
}

.recent-loan__meta {
  margin-top: 8px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.meta-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid var(--border-ltr);
  background: var(--background);
  font-size: 10.5px;
  font-weight: 950;
}

.recent-loan__num i.fa-duotone {
  --fa-primary-color: rgba(40, 110, 255, 0.95);
  --fa-secondary-color: rgba(40, 110, 255, 0.30);
}

.meta-chip i.fa-duotone.fa-sack-dollar {
  --fa-primary-color: rgba(10, 170, 120, 0.95);
  --fa-secondary-color: rgba(10, 170, 120, 0.30);
}

.meta-chip i.fa-duotone.fa-location-dot {
  --fa-primary-color: rgba(140, 90, 255, 0.95);
  --fa-secondary-color: rgba(140, 90, 255, 0.28);
}

.meta-chip:has(.fa-sack-dollar) {
  border-color: rgba(10, 170, 120, 0.25);
  background: rgba(10, 170, 120, 0.06);
}

.meta-chip:has(.fa-location-dot) {
  border-color: rgba(140, 90, 255, 0.25);
  background: rgba(140, 90, 255, 0.06);
}
.meta-chip--type i.fa-duotone {
  --fa-primary-color: rgba(29, 78, 216, 0.95);
  --fa-secondary-color: rgba(29, 78, 216, 0.30);
}

.meta-chip--purpose i.fa-duotone {
  --fa-primary-color: rgba(37, 99, 235, 0.90);
  --fa-secondary-color: rgba(37, 99, 235, 0.22);
}

</style>
